import { getMapsLibrary } from './utils/getMapsLibrary';
import debounce from 'lodash/debounce';
import trackPinPreviewClicked from 'segment/events/search-pin-preview-clicked';
import { isMobilePortraitView } from 'hooks/use-is-mobile';
import { convertRatingToSlug } from 'store/filters/rating-filter-utils';
import { initializeMap } from './utils/initializeMap';
import { cleanupMap } from './utils/cleanupMap';
import { updateMarker } from './utils/updateMarker';
import { removeMarker } from './utils/removeMarker';
import { addMarker } from './utils/addMarker';
import { newMarkerAndLatLng } from './utils/newMarkerAndLatLng';
import { fitMapToMarkers as fitMapToMarkersUtil } from './utils/fitMapToMarkers';
import { returnMapToDestination as returnMapToDestinationUtil } from './utils/returnMapToDestination';
import { setZoom as setZoomUtil } from './utils/setZoom';
import { addKMLLayer as addKMLLayerUtil } from './utils/addKMLLayer';
import { panTo as panToUtil } from './utils/panTo';
import { getMapOptions } from './utils/getMapOptions';
import { hideMarker } from './utils/showHideMarker';
import { computePinZIndices } from 'common/search-results-map/spot-map/spot-map-utils';
import { DEBOUNCE_SETTINGS } from 'common/constants';
import { MANUAL_ZOOM_IN_EVENT, MANUAL_ZOOM_OUT_EVENT, RETURN_MAP_TO_CENTER_EVENT, } from 'utils/window-events/constants';
// Note: The googleMapWidget function uses function declarations instead of arrow functions so that the functions
// are hoisted and can be used before they are defined to improve readability.
/* eslint-disable @typescript-eslint/no-use-before-define */
function enhanceSpotMarkers(spots, highlightedSpotId, previewedSpotId) {
    if (!spots)
        return [];
    const zIndexMap = computePinZIndices(spots);
    return spots.map((spot, index) => {
        return {
            ...spot,
            isHighlighted: `${highlightedSpotId}` === `${spot.spotId}`,
            isPreviewed: `${previewedSpotId}` === `${spot.spotId}`,
            rank: String(index),
            priorityZIndex: zIndexMap.get(spot.spotId) ?? 0,
        };
    });
}
/**
 * Creates a new GoogleMapWidget instance with the specified options.
 *
 * Note: All the [set*] functions are to be used only by the parent React component.
 * Do NOT use them in the GoogleMapWidget instance as they will not update the state in the parent.
 * Use [on*] callbacks to update the state in the parent.
 */
const googleMapWidget = ({ container, googleMapsId, centerLatLng: _centerLatLng, centerTitle: _centerTitle, kmlMapLayerEnabled, kmlMapLayerURL, searchRequestId: _searchRequestId, zoom: _zoom, onPan: onPanCallback, onZoom, onGoogleMapLoaded, showAllSpots, isMobile: _isMobile, highlightedSpotId: _highlightedSpotId, previewedSpotId: _previewedSpotId, onSpotHighlighted, onSpotPreviewed, ratingFilters: _ratingFilters, }) => {
    const maps = getMapsLibrary();
    const markers = {};
    const mapAction = (action) => {
        if (action === 'zoom') {
            onZoomChanged();
        }
        else if (action === 'pan') {
            onPan();
        }
    };
    const mapActionDebounce = debounce(mapAction, ...DEBOUNCE_SETTINGS);
    let highlightedSpotId = _highlightedSpotId;
    let previewedSpotId = _previewedSpotId;
    let centerLatLng = _centerLatLng;
    let centerMarker = null;
    let centerTitle = _centerTitle;
    let isDraggingTimeout = null;
    let isDragging = false;
    let popup = null;
    let kmlLayer = null;
    let map = null;
    let needsFitToBounds = true;
    let searchRequestId = _searchRequestId;
    let zoom = _zoom;
    let prevZoom = null;
    let isMobile = _isMobile;
    let spots = [];
    let ratingFilters = _ratingFilters;
    const prevRatingFilters = _ratingFilters;
    let lastMapCenter = null;
    let lastMapZoom = null;
    let isProgrammaticChange = false;
    let isInteracting = false;
    /**
     * Callback function to be called when the map becomes idle after user interaction.
     * Determines whether the user panned, zoomed, or both, and triggers the appropriate action.
     * We check both flags because:
     *   - isInteracting: true when user physically interacts (drag/zoom)
     *   - isProgrammaticChange: true when our code changes map (fitMapToMarkers, etc.)
     * We only want to search when user interacted AND it wasn't our code that triggered the change
     * isInteracting could be changed to isInteracting as it programmatically triggers as well hence the need for both
     */
    function idleCallback() {
        if (isInteracting && !isProgrammaticChange) {
            const currentCenter = map.getCenter();
            const currentZoom = map.getZoom();
            // Calculate center change distance in degrees
            const latDiff = lastMapCenter
                ? Math.abs(lastMapCenter.lat() - currentCenter.lat())
                : 0;
            const lngDiff = lastMapCenter
                ? Math.abs(lastMapCenter.lng() - currentCenter.lng())
                : 0;
            // Threshold for "significant" pan - roughly 50 meters at mid-latitudes
            // 0.0005 degrees ≈ 55 meters at equator, less at higher latitudes
            const PAN_THRESHOLD = 0.0005;
            const centerChanged = lastMapCenter &&
                (latDiff > PAN_THRESHOLD || lngDiff > PAN_THRESHOLD);
            const zoomChanged = lastMapZoom !== null && lastMapZoom !== currentZoom;
            if (centerChanged && !zoomChanged) {
                mapActionDebounce('pan'); // Pure pan -> loadSpotsTransientV2
            }
            else if (zoomChanged && !centerChanged) {
                mapActionDebounce('zoom'); // Pure zoom -> loadMoreSpotsTransientV2
            }
            else if (centerChanged && zoomChanged) {
                mapActionDebounce('pan'); // Combined -> treat as pan (loadSpotsTransientV2)
            }
        }
        // Always update tracking regardless of whether we triggered search
        const currentCenter = map.getCenter();
        lastMapCenter = currentCenter;
        lastMapZoom = map.getZoom();
        isProgrammaticChange = false;
        isInteracting = false;
    }
    /**
     * Sets the user interaction flag when user starts interacting with the map.
     */
    function setUserInteracting() {
        isInteracting = true;
    }
    const eventZoomIn = () => {
        map.setZoom(map.getZoom() + 1);
    };
    const eventZoomOut = () => {
        map.setZoom(map.getZoom() - 1);
    };
    /**
     * Initializes the Google Map instance.
     */
    function init() {
        container.innerHTML = '';
        map = initializeMap({
            container,
            googleMapsId,
            maps,
            zoom,
            center: centerLatLng,
            initialIdleCallback: () => {
                onGoogleMapLoaded(returnMapToDestination);
            },
            idle: idleCallback,
            tilesLoadedCallback: () => {
                if (spots.length > 0) {
                    fitMapToMarkers();
                }
            },
            onDragStart: () => {
                setUserInteracting();
                onDragStart();
            },
            onDragEnd: () => {
                setUserInteracting();
                onDragEnd();
            },
            onZoomChanged: () => {
                setUserInteracting();
                // Don't call zoomDebounce here - let idleCallback handle it
            },
            onMapClick: () => {
                closePopup();
            },
        });
        if (kmlMapLayerEnabled && kmlMapLayerURL) {
            addKMLLayer(kmlMapLayerURL);
        }
        // add center marker
        const { marker } = newMarkerAndLatLng({
            lat: centerLatLng.lat,
            lng: centerLatLng.lng,
            title: centerTitle,
            maps,
            map,
        });
        centerMarker = marker;
        // Event listener to support SpotMapControls
        window.addEventListener(MANUAL_ZOOM_IN_EVENT, eventZoomIn);
        window.addEventListener(MANUAL_ZOOM_OUT_EVENT, eventZoomOut);
        // Event listener to support SpotListNoResults
        window.addEventListener(RETURN_MAP_TO_CENTER_EVENT, returnMapToDestination);
    }
    /**
     * Returns the map to the destination center and zoom level.
     */
    function returnMapToDestination() {
        if (!map)
            return;
        needsFitToBounds = true;
        isProgrammaticChange = true; // Set flag before programmatic change
        mapActionDebounce('pan');
        returnMapToDestinationUtil({
            map,
            center: centerLatLng,
        });
    }
    /**
     * Callback function to be called when the map is panned.
     */
    function onPan() {
        if (!map)
            return;
        const options = getMapOptions({
            map,
            center: centerLatLng,
        });
        onPanCallback(options);
    }
    /**
     * Sets the center coordinates and title of the map.
     *
     * @param {number} lat - The latitude of the new center coordinates.
     * @param {number} lng - The longitude of the new center coordinates.
     * @param {string} title - The new title of the center marker.
     */
    function setCenterLatLngAndTitle(lat, lng, title) {
        if (centerLatLng.lat === lat &&
            centerLatLng.lng === lng &&
            centerTitle === title) {
            return;
        }
        const { position, marker } = newMarkerAndLatLng({
            lat,
            lng,
            title,
            maps,
            map,
        });
        centerLatLng = position;
        centerTitle = title;
        // clear old marker
        if (centerMarker) {
            hideMarker({ marker: centerMarker });
        }
        centerMarker = marker;
    }
    /**
     * Sets the zoom level of the map.
     *
     * @param {number} newZoom - The new zoom level of the map.
     */
    function setZoom(newZoom) {
        if (zoom !== newZoom && map) {
            prevZoom = zoom;
            zoom = newZoom;
            closePopup();
            setZoomUtil({ zoom, map });
        }
    }
    /**
     * Sets the ID of the search request.
     *
     * @param {string} newSearchRequestId - The new ID of the search request.
     * @returns {boolean} Whether the search request ID was changed.
     */
    function setSearchRequestId(newSearchRequestId) {
        if (searchRequestId !== newSearchRequestId) {
            searchRequestId = newSearchRequestId;
            needsFitToBounds = true;
            return true;
        }
        return false;
    }
    /**
     * Refreshes the spots displayed on the map with the specified new spots.
     *
     * @param {Array} newSpots - The new spots to display on the map.
     * @param {boolean} shouldAdjustMarkers - Whether the markers should be adjusted to fit the map bounds.
     * @returns {void}
     */
    function refreshSpots(newSpots, shouldAdjustMarkers = true) {
        const previousSpots = spots;
        const newEnhancedSpots = enhanceSpotMarkers(newSpots, highlightedSpotId, previewedSpotId);
        const { removed, added, updated } = getSpotChanges(previousSpots, newEnhancedSpots, isMobile);
        const areSpotsChanged = removed.length > 0 || added.length > 0 || updated.length > 0;
        if (areSpotsChanged) {
            // first remove the markers
            removed.forEach(({ spotId }) => {
                removeSpotMarker(spotId);
            });
            // then add the markers
            added.forEach(spot => {
                addSpotMarker(spot);
            });
            // then refresh the updated markers
            updated.forEach(spot => {
                updateSpotMarker(spot);
            });
            spots = newEnhancedSpots;
            if (shouldAdjustMarkers && needsFitToBounds) {
                fitMapToMarkers();
            }
        }
    }
    /**
     * Adds a marker for the specified spot to the map.
     *
     * @param {object} spot - The spot to add a marker for.
     */
    function addSpotMarker(spot) {
        const { spotId, isHighlighted } = spot;
        if (markers[spotId]) {
            return;
        }
        const handleOnMouseEnter = (hoveredSpotId) => {
            // Desktop Only
            if (!isMobile) {
                onSpotHighlighted({ spotId: hoveredSpotId });
            }
        };
        const handleOnMouseLeave = () => {
            // Desktop Only
            if (!isMobile) {
                onSpotHighlighted({ spotId: '' });
            }
        };
        markers[spotId] = addMarker({
            spot,
            map,
            maps,
            isMobile,
            isHighlighted,
            onMouseEnter: handleOnMouseEnter,
            onMouseLeave: handleOnMouseLeave,
            onClick: onMarkerClicked,
            onShowPopup: newPopup => {
                if (popup) {
                    closePopup();
                }
                popup = newPopup;
            },
            ratingFilters,
        });
    }
    /**
     * Updates the marker for the specified spot on the map.
     *
     * @param {SpotMarkerEnhanced} spot - The spot to update .
     */
    function updateSpotMarker(spot) {
        const marker = markers[spot.spotId];
        if (marker) {
            updateMarker({
                marker,
                spot,
                isMobile,
                map,
                ratingFilters,
            });
        }
    }
    /*
     * Handle click on a marker.
     * @param {Spot} spot - The spot.
     */
    function onMarkerClicked(spot) {
        const spotId = spot?.spotId || '';
        const rank = spot?.rank;
        onSpotPreviewed({ spotId });
        trackPinPreviewClicked({
            spotId,
            rank,
        });
    }
    /**
     * Removes the marker for the specified spot from the map.
     *
     * @param {string} spotId - The ID of the spot to remove the marker for.
     */
    function removeSpotMarker(spotId) {
        const marker = markers[spotId];
        if (marker) {
            removeMarker({ marker, maps });
            delete markers[spotId];
        }
    }
    /**
     * Removes all spot markers from the map.
     */
    function removeAllMarkers() {
        for (const spotId of Object.keys(markers)) {
            removeSpotMarker(spotId);
        }
    }
    /**
     * Callback function to be called when the user starts dragging the map.
     */
    function onDragStart() {
        isDragging = true;
        if (isDraggingTimeout) {
            clearTimeout(isDraggingTimeout);
            isDraggingTimeout = null;
        }
    }
    /**
     * Callback function to be called when the user stops dragging the map.
     */
    function onDragEnd() {
        if (isDragging) {
            isDraggingTimeout = setTimeout(() => {
                isDragging = false;
                closePopup();
            }, 500);
        }
    }
    /**
     * Fits the map to the markers displayed on it.
     */
    function fitMapToMarkers() {
        needsFitToBounds = false;
        if (spots.length > 0) {
            const distance = getDistanceOfTheSpotsFromDestination();
            isProgrammaticChange = true; // Set flag before programmatic change
            fitMapToMarkersUtil({
                distance,
                center: centerLatLng,
                maps,
                map,
            });
        }
    }
    /**
     * Callback function to be called when the user zooms in or out of the map.
     */
    function onZoomChanged() {
        if (!map)
            return;
        const options = getMapOptions({
            map,
            center: centerLatLng,
        });
        // the 'zoom_changed' event gets fired in every zoom instance, so we have to determine what to do with zoom levels in parent
        onZoom({
            ...options,
            prevZoom,
        });
    }
    /**
     * Cleans up the Google Map instance by removing all event listeners and removing the onSpotHover listener from the document.
     */
    function cleanup() {
        if (maps && map) {
            removeAllMarkers();
            cleanupMap({
                map,
                maps,
            });
            map = null;
            popup = null;
            centerMarker = null;
            spots = [];
        }
        if (isDraggingTimeout) {
            clearTimeout(isDraggingTimeout);
        }
        if (mapActionDebounce) {
            mapActionDebounce.cancel();
        }
        window.removeEventListener(RETURN_MAP_TO_CENTER_EVENT, returnMapToDestination);
        window.removeEventListener(MANUAL_ZOOM_IN_EVENT, eventZoomIn);
        window.removeEventListener(MANUAL_ZOOM_OUT_EVENT, eventZoomOut);
    }
    /**
     * Gets the changes in spots between the previous and new spots.
     *
     * @param {Array} newSpots - The new spots to compare with the previous spots.
     * @param {Array} prevSpots - The previous spots to compare with the new spots.
     * @returns {object} An object containing the removed and updated spots.
     */
    function getSpotChanges(prevSpots, newSpots, isMobileView) {
        // removed are spots that are in prevSpots but not in newSpots or whose isPreviewed property has changed
        const removed = prevSpots.filter(prevSpot => newSpots.every(newSpot => newSpot.spotId !== prevSpot.spotId));
        // newly added are spots that are in newSpots but not in prevSpots
        const added = newSpots.filter(newSpot => prevSpots.every(prevSpot => prevSpot.spotId !== newSpot.spotId));
        // on mobile (map) we only care about preview
        // but on desktop we have to account for both highlight (hover) and preview
        const mobilePredicate = (a, b) => a.isPreviewed !== b.isPreviewed;
        const desktopPredicate = (a, b) => a.isHighlighted !== b.isHighlighted ||
            a.isPreviewed !== b.isPreviewed;
        const predicate = isMobileView ? mobilePredicate : desktopPredicate;
        // updated are spots that are in both prevSpots and newSpots but have different properties
        const updated = newSpots.filter(newSpot => prevSpots.some(prevSpot => newSpot.spotId === prevSpot.spotId &&
            (newSpot.markerLabelText !== prevSpot.markerLabelText ||
                newSpot.recentlyViewed !== prevSpot.recentlyViewed ||
                newSpot.showRatingColor !== prevSpot.showRatingColor ||
                predicate(newSpot, prevSpot))));
        return { removed, added, updated };
    }
    /**
     * Gets the distance from the destination to the spot that contains ~50% of spots.
     *
     * @returns {number} The distance from the destination to the spot that contains ~50% of spots.
     */
    function getDistanceOfTheSpotsFromDestination() {
        const sortedByDistance = spots.sort((a, b) => a.distanceFromDestination - b.distanceFromDestination);
        // get the distance from the destination
        // to the spot that contains ~50% of spots
        // or 90% of spots when showAllSpots flag is set
        const spotsToShow = showAllSpots ? 1 : 0.5;
        const index = Math.floor((spots.length - 1) * spotsToShow);
        // only scale the distance on desktop
        if (!isMobilePortraitView() && !showAllSpots) {
            return sortedByDistance[index].distanceFromDestination * 1.5; // scale slightly to avoid clipping in the header. 1.5 is a magic number
        }
        return sortedByDistance[index].distanceFromDestination;
    }
    /**
     * Adds a KML layer to the map.
     *
     * @param {string} kmlUrl - The URL of the KML file to add to the map.
     */
    function addKMLLayer(kmlUrl) {
        if (!kmlLayer) {
            kmlLayer = addKMLLayerUtil({
                map,
                maps,
                kmlUrl,
            });
        }
    }
    /**
     * Closes the popup if it is open.
     */
    function closePopup() {
        if (popup) {
            popup?.setMap(null);
            popup = null;
            // Notify parent React component to clear the highlighted spot
            // On the next React render the highlighted spot is cleared and shown as normal
            onSpotHighlighted({ spotId: '' });
            onSpotPreviewed({ spotId: '' });
        }
    }
    /**
     * Updates the internal highlightedSpotId state to match with the parent React component.
     *
     * @param {string} spotId - The ID of the spot to highlight.
     */
    function setHighlightedSpotId(spotId) {
        highlightedSpotId = spotId;
    }
    /**
     * Updates the internal isMobile state to match with the parent React component.
     *
     * @param {boolean} newIsMobileValue - The new value of isMobile.
     */
    function setIsMobile(newIsMobileValue) {
        isMobile = newIsMobileValue;
    }
    /**
     * Updates the internal previewedSpotId state to match with the parent React component.
     *
     * @param {string} spotId - The ID of the spot to preview.
     */
    function setPreviewedSpotId(spotId) {
        previewedSpotId = spotId;
    }
    /**
     * Pans the map to the spot with the specified ID.
     *
     * @param {string} spotId - The ID of the spot to pan to.
     */
    function panTo(spotId) {
        if (isDragging || !map) {
            return;
        }
        const spot = spots.find(s => s.spotId === spotId);
        if (spot) {
            panToUtil({
                map,
                position: {
                    lat: spot.latitude,
                    lng: spot.longitude,
                },
            });
        }
    }
    function setRatingFilters(activeRatingTypes) {
        if (prevRatingFilters !== activeRatingTypes) {
            ratingFilters = activeRatingTypes || [];
            spots.forEach(spot => {
                spot.showRatingColor = ratingFilters.includes(convertRatingToSlug(spot?.rating));
            });
        }
    }
    init();
    return {
        addKMLLayer,
        cleanup,
        getSpotChanges,
        refreshSpots,
        panTo,
        setCenterLatLngAndTitle,
        setSearchRequestId,
        setHighlightedSpotId,
        setPreviewedSpotId,
        setIsMobile,
        setZoom,
        closePopup,
        setRatingFilters,
        _onDragStart: onDragStart, // for testing purpose only
    };
};
export default googleMapWidget;
/* eslint-enable @typescript-eslint/no-use-before-define */
